import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowLeft } from "lucide-react";
import { AppLayout } from "../../../components/layout";
import { ContentCard } from "../../../components/ui";
import { inventoryManagementService } from "../../../services/inventoryManagementService";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../components/ui/select";

export function RecordInventoryInPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [successToast, setSuccessToast] = useState("");

  // Options states
  const [products, setProducts] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [customers, setCustomers] = useState([]);

  // Form states
  const [sourceType, setSourceType] = useState("Purchase"); // Default to Purchase
  const [formData, setFormData] = useState({
    productId: "",
    batchNumber: "",
    receiptDate: "2026-08-04", // Default current mockup date
    returnDate: "2026-08-04",
    receivedQty: 0,
    defectiveQty: 0,
    warrantyStartDate: "",
    warrantyEndDate: "",
    vendorId: "",
    customerId: "",
    poNumber: "",
    vendorInvoiceNumber: "",
    customerInvoiceId: "",
    invoiceDate: "",
    projectName: "",
    iofNumber: "",
    warehouseId: "",
  });

  const [formErrors, setFormErrors] = useState({});

  // Load lookup metadata
  useEffect(() => {
    let active = true;
    setLoading(true);

    Promise.all([
      inventoryManagementService.getInventoryProducts(),
      inventoryManagementService.getWarehouses(),
      inventoryManagementService.getVendors(),
      inventoryManagementService.getCustomers(),
    ])
      .then(([p, w, v, c]) => {
        if (!active) return;
        setProducts(p);
        setWarehouses(w);
        setVendors(v);
        setCustomers(c);
        setLoading(false);
      })
      .catch((err) => {
        if (!active) return;
        console.error("Failed to load receipt form lookups:", err);
        setError(t("validation.failedToLoadDropdownMetadata"));
        setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [t]);

  const handleChange = (key, value) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
    // Clear field-specific error
    if (formErrors[key]) {
      setFormErrors((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    }
  };

  const handleSourceTypeChange = (value) => {
    setSourceType(value);
    // Reset product and warehouse selections when type switches to prevent validation carryover
    setFormData((prev) => ({
      ...prev,
      productId: "",
      batchNumber: "",
      vendorId: "",
      customerId: "",
      customerInvoiceId: "",
      poNumber: "",
      vendorInvoiceNumber: "",
      iofNumber: "",
      projectName: "",
    }));
    setFormErrors({});
  };

  // Accepted Qty computation: Received - Defective
  const acceptedQty = useMemo(() => {
    const rx = Number(formData.receivedQty) || 0;
    const df = Number(formData.defectiveQty) || 0;
    return Math.max(0, rx - df);
  }, [formData.receivedQty, formData.defectiveQty]);

  // Inline validation for quantities
  const qtyError = useMemo(() => {
    const rx = Number(formData.receivedQty) || 0;
    const df = Number(formData.defectiveQty) || 0;
    if (df > rx) {
      return t("validation.defectiveExceedsReceived");
    }
    return null;
  }, [formData.receivedQty, formData.defectiveQty, t]);

  const selectedProduct = useMemo(() => {
    return products.find((p) => p.id === formData.productId);
  }, [products, formData.productId]);

  const selectedWarehouse = useMemo(() => {
    return warehouses.find((w) => w.id === formData.warehouseId);
  }, [warehouses, formData.warehouseId]);

  const selectedVendor = useMemo(() => {
    return vendors.find((v) => v.id === formData.vendorId);
  }, [vendors, formData.vendorId]);

  const selectedCustomer = useMemo(() => {
    return customers.find((c) => c.id === formData.customerId);
  }, [customers, formData.customerId]);

  const handleCancel = () => {
    navigate("/inventory-management/stock-position");
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const errors = {};

    // Validate common fields
    if (!formData.productId) {
      errors.productId = t("validation.fieldRequired", { field: t("inventory.product") });
    }
    if (!formData.warehouseId) {
      errors.warehouseId = t("validation.fieldRequired", { field: t("inventory.warehouseLocation") });
    }
    if (Number(formData.receivedQty) <= 0) {
      errors.receivedQty = t("validation.qtyMustBeGreaterThanZero", {
        field: t("inventory.receivedQuantity"),
      });
    }
    if (qtyError) {
      errors.defectiveQty = qtyError;
    }

    // Validate type-specific fields
    if (sourceType === "Purchase") {
      if (!formData.batchNumber) {
        errors.batchNumber = t("validation.fieldRequired", { field: t("inventory.batchNumber") });
      }
      if (!formData.receiptDate) {
        errors.receiptDate = t("validation.fieldRequired", { field: t("dateTime.receiptDate") });
      }
      if (!formData.vendorId) {
        errors.vendorId = t("validation.fieldRequired", { field: t("inventory.vendorName") });
      }
    } else {
      if (!formData.customerId) {
        errors.customerId = t("validation.fieldRequired", { field: t("inventory.customerName") });
      }
      if (!formData.customerInvoiceId) {
        errors.customerInvoiceId = t("validation.fieldRequired", {
          field: t("inventory.customerInvoiceId"),
        });
      }
      if (!formData.returnDate) {
        errors.returnDate = t("validation.fieldRequired", { field: t("dateTime.returnDate") });
      }
    }

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    setSubmitting(true);
    const payload = {
      sourceType,
      productId: formData.productId,
      productName: selectedProduct?.name,
      partNumber: selectedProduct?.partNumber,
      warehouseId: formData.warehouseId,
      warehouseName: selectedWarehouse?.name,
      receivedQuantity: Number(formData.receivedQty),
      defectiveQuantity: Number(formData.defectiveQty),
      acceptedQuantity: acceptedQty,
      warrantyStartDate: formData.warrantyStartDate || null,
      warrantyEndDate: formData.warrantyEndDate || null,
      ...(sourceType === "Purchase"
        ? {
            batchNumber: formData.batchNumber,
            receiptDate: formData.receiptDate,
            vendorId: formData.vendorId,
            vendorName: selectedVendor?.name,
            poNumber: formData.poNumber || null,
            vendorInvoiceNumber: formData.vendorInvoiceNumber || null,
            invoiceDate: formData.invoiceDate || null,
            iofNumber: formData.iofNumber || null,
          }
        : {
            batchNumber: formData.batchNumber || `RET-${Date.now()}`,
            returnDate: formData.returnDate,
            customerId: formData.customerId,
            customerName: selectedCustomer?.name,
            customerInvoiceId: formData.customerInvoiceId,
            projectName: formData.projectName || null,
          }),
    };

    inventoryManagementService.saveInventoryReceipt(payload)
      .then(() => {
        setSuccessToast(t("inventory.inventoryRecordedSuccess"));
        // Reset form
        setFormData({
          productId: "",
          batchNumber: "",
          receiptDate: "2026-08-04",
          returnDate: "2026-08-04",
          receivedQty: 0,
          defectiveQty: 0,
          warrantyStartDate: "",
          warrantyEndDate: "",
          vendorId: "",
          customerId: "",
          poNumber: "",
          vendorInvoiceNumber: "",
          customerInvoiceId: "",
          invoiceDate: "",
          projectName: "",
          iofNumber: "",
          warehouseId: "",
        });
        setSubmitting(false);
        // Clear success toast after 3 seconds
        setTimeout(() => setSuccessToast(""), 3000);
      })
      .catch((err) => {
        console.error("Failed to save inventory receipt:", err);
        setError(t("validation.inventoryRecordSaveError"));
        setSubmitting(false);
      });
  };

  return (
    <AppLayout
      breadcrumb={[
        { label: t("inventory.operations") },
        {
          label: t("inventory.inventoryManagement"),
          onClick: () => navigate("/inventory-management/stock-position"),
        },
        { label: t("inventory.recordInventoryIn") },
      ]}
      className="space-y-6"
    >
      {/* Toast Alert Success popup */}
      {successToast && (
        <div className="fixed top-5 right-5 z-50 bg-emerald-600 text-white px-5 py-3 rounded-xl shadow-lg flex items-center gap-2 animate-in fade-in duration-300 font-sans font-bold text-xs">
          <span>✔️</span>
          <span>{successToast}</span>
        </div>
      )}

      {/* Custom Title and Back Nav */}
      <div className="space-y-1 font-sans">
        <button
          onClick={handleCancel}
          className="inline-flex items-center gap-1 text-[var(--color-primary)] text-xs font-bold hover:underline bg-transparent border-0 cursor-pointer focus:outline-none mb-2"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          <span>{t("formActions.backToStockJournal")}</span>
        </button>

        <h1 className="text-xl sm:text-2xl font-extrabold text-foreground tracking-tight">
          {t("inventory.recordInventoryInTitle")}
        </h1>
      </div>

      {loading ? (
        /* Form loading skeleton full width */
        <ContentCard bodyClassName="p-6 h-[400px] animate-pulse bg-gray-50/20" />
      ) : error ? (
        <div className="p-8 text-center text-[var(--color-error)] font-semibold text-sm border bg-white rounded-2xl">
          ⚠️ {error}
        </div>
      ) : (
        /* Main Form Card covering workspace fully */
        <ContentCard bodyClassName="p-6 font-sans">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-x-6 gap-y-5">
              {/* 1. Source type Selection */}
              <div className="space-y-1.5 col-span-1">
                <label className="text-[11px] font-bold uppercase tracking-wider text-[var(--color-text-secondary)]">
                  {t("inventory.sourceType")} <span className="text-rose-500">*</span>
                </label>
                <Select value={sourceType} onValueChange={handleSourceTypeChange}>
                  <SelectTrigger className="h-10 rounded-xl border border-[var(--color-text-border)] bg-white text-xs font-semibold focus:ring-[var(--color-accent)] w-full">
                    <SelectValue placeholder={t("fsm.selectType")} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Purchase">{t("inventory.purchase")}</SelectItem>
                    <SelectItem value="Excess Material Return">
                      {t("inventory.excessMaterialReturn")}
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="md:col-span-2" />

              {/* 2. Product Dropdown Selection */}
              <div className="space-y-1.5 col-span-1">
                <label className="text-[11px] font-bold uppercase tracking-wider text-[var(--color-text-secondary)]">
                  {t("inventory.product")} <span className="text-rose-500">*</span>
                </label>
                <Select
                  value={formData.productId}
                  onValueChange={(val) => handleChange("productId", val)}
                >
                  <SelectTrigger
                    className={`h-10 rounded-xl border bg-white text-xs font-semibold focus:ring-[var(--color-accent)] w-full ${
                      formErrors.productId ? "border-rose-500" : "border-[var(--color-text-border)]"
                    }`}
                  >
                    <SelectValue placeholder={t("inventory.selectProduct")} />
                  </SelectTrigger>
                  <SelectContent>
                    {products.map((p) => {
                      const isDisabled =
                        sourceType === "Purchase" && p.status === "End of Production";
                      return (
                        <SelectItem key={p.id} value={p.id} disabled={isDisabled}>
                          {p.name}
                          {isDisabled ? ` (${t("inventory.endOfProduction")})` : ""}
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
                {sourceType === "Purchase" ? (
                  <span className="text-[10px] text-gray-400 block mt-1">
                    {t("inventory.endOfProductionBlocked")}
                  </span>
                ) : (
                  selectedProduct?.status === "End of Production" && (
                    <span className="text-[10px] text-amber-600 font-bold block mt-1">
                      {t("inventory.endOfProductionAllowed")}
                    </span>
                  )
                )}
                {formErrors.productId && (
                  <p className="text-[11px] text-rose-500 font-semibold mt-1">{formErrors.productId}</p>
                )}
              </div>

              {/* 3. Batch number Input */}
              <div className="space-y-1.5 col-span-1">
                <label className="text-[11px] font-bold uppercase tracking-wider text-[var(--color-text-secondary)]">
                  {t("inventory.batchNumber")}{" "}
                  {sourceType === "Purchase" ? <span className="text-rose-500">*</span> : null}
                </label>
                <input
                  type="text"
                  value={formData.batchNumber}
                  onChange={(e) => handleChange("batchNumber", e.target.value)}
                  placeholder={sourceType === "Purchase" ? "" : t("inventory.leaveBlankAutoGenerate")}
                  className={`w-full h-10 px-3 rounded-xl border bg-white text-xs font-semibold focus:border-[var(--color-accent)] focus:outline-none focus:ring-2 focus:ring-[var(--color-accent)]/10 ${
                    formErrors.batchNumber ? "border-rose-500" : "border-[var(--color-text-border)]"
                  }`}
                />
                {sourceType !== "Purchase" && (
                  <span className="text-[10px] text-gray-400 block mt-1">
                    {t("inventory.autoBatchHint")}
                  </span>
                )}
                {formErrors.batchNumber && (
                  <p className="text-[11px] text-rose-500 font-semibold mt-1">{formErrors.batchNumber}</p>
                )}
              </div>

              {/* 4. Date Picker Input (Receipt or Return) */}
              <div className="space-y-1.5 col-span-1">
                <label className="text-[11px] font-bold uppercase tracking-wider text-[var(--color-text-secondary)]">
                  {sourceType === "Purchase" ? t("dateTime.receiptDate") : t("dateTime.returnDate")}{" "}
                  <span className="text-rose-500">*</span>
                </label>
                <input
                  type="date"
                  value={sourceType === "Purchase" ? formData.receiptDate : formData.returnDate}
                  onChange={(e) =>
                    handleChange(sourceType === "Purchase" ? "receiptDate" : "returnDate", e.target.value)
                  }
                  className={`w-full h-10 px-3 rounded-xl border bg-white text-xs font-semibold focus:border-[var(--color-accent)] focus:outline-none focus:ring-2 focus:ring-[var(--color-accent)]/10 ${
                    formErrors.receiptDate || formErrors.returnDate
                      ? "border-rose-500"
                      : "border-[var(--color-text-border)]"
                  }`}
                />
                {formErrors.receiptDate && (
                  <p className="text-[11px] text-rose-500 font-semibold mt-1">{formErrors.receiptDate}</p>
                )}
                {formErrors.returnDate && (
                  <p className="text-[11px] text-rose-500 font-semibold mt-1">{formErrors.returnDate}</p>
                )}
              </div>

              {/* 5. Received quantity input */}
              <div className="space-y-1.5 col-span-1">
                <label className="text-[11px] font-bold uppercase tracking-wider text-[var(--color-text-secondary)]">
                  {t("inventory.receivedQuantity")} <span className="text-rose-500">*</span>
                </label>
                <input
                  type="number"
                  min="0"
                  value={formData.receivedQty}
                  onChange={(e) => handleChange("receivedQty", Number(e.target.value))}
                  className={`w-full h-10 px-3 rounded-xl border bg-white text-xs font-semibold focus:border-[var(--color-accent)] focus:outline-none focus:ring-2 focus:ring-[var(--color-accent)]/10 ${
                    formErrors.receivedQty ? "border-rose-500" : "border-[var(--color-text-border)]"
                  }`}
                />
                {formErrors.receivedQty && (
                  <p className="text-[11px] text-rose-500 font-semibold mt-1">{formErrors.receivedQty}</p>
                )}
              </div>

              {/* 6. Defective quantity input */}
              <div className="space-y-1.5 col-span-1">
                <label className="text-[11px] font-bold uppercase tracking-wider text-[var(--color-text-secondary)]">
                  {t("inventory.defectiveQuantity")} <span className="text-rose-500">*</span>
                </label>
                <input
                  type="number"
                  min="0"
                  value={formData.defectiveQty}
                  onChange={(e) => handleChange("defectiveQty", Number(e.target.value))}
                  className={`w-full h-10 px-3 rounded-xl border bg-white text-xs font-semibold focus:border-[var(--color-accent)] focus:outline-none focus:ring-2 focus:ring-[var(--color-accent)]/10 ${
                    formErrors.defectiveQty ? "border-rose-500" : "border-[var(--color-text-border)]"
                  }`}
                />
                {formErrors.defectiveQty && (
                  <p className="text-[11px] text-rose-500 font-semibold mt-1">{formErrors.defectiveQty}</p>
                )}
              </div>

              {/* 7. Accepted quantity (Read Only computed) */}
              <div className="space-y-1.5 col-span-1">
                <label className="text-[11px] font-bold uppercase tracking-wider text-[var(--color-text-secondary)]">
                  {t("inventory.acceptedQuantity")}
                </label>
                <div className="w-full h-10 px-3 rounded-xl border border-gray-200 bg-gray-50 flex items-center text-xs font-extrabold text-foreground font-mono">
                  {acceptedQty}
                </div>
                <span className="text-[10px] text-gray-400 block mt-1">
                  {t("inventory.acceptedQtyHint")}
                </span>
              </div>

              {/* 8. Warranty start date */}
              <div className="space-y-1.5 col-span-1">
                <label className="text-[11px] font-bold uppercase tracking-wider text-[var(--color-text-secondary)]">
                  {t("inventory.warrantyStart")}
                </label>
                <input
                  type="date"
                  value={formData.warrantyStartDate}
                  onChange={(e) => handleChange("warrantyStartDate", e.target.value)}
                  className="w-full h-10 px-3 rounded-xl border border-[var(--color-text-border)] bg-white text-xs font-semibold focus:border-[var(--color-accent)] focus:outline-none focus:ring-2 focus:ring-[var(--color-accent)]/10"
                />
              </div>

              {/* 9. Warranty end date */}
              <div className="space-y-1.5 col-span-1">
                <label className="text-[11px] font-bold uppercase tracking-wider text-[var(--color-text-secondary)]">
                  {t("dateTime.warrantyEndDate2")}
                </label>
                <input
                  type="date"
                  value={formData.warrantyEndDate}
                  onChange={(e) => handleChange("warrantyEndDate", e.target.value)}
                  className="w-full h-10 px-3 rounded-xl border border-[var(--color-text-border)] bg-white text-xs font-semibold focus:border-[var(--color-accent)] focus:outline-none focus:ring-2 focus:ring-[var(--color-accent)]/10"
                />
                <span className="text-[10px] text-gray-400 block mt-1">
                  {t("inventory.fefoSortNote")}
                </span>
              </div>

              {/* 10. Vendor or Customer dropdown */}
              <div className="space-y-1.5 col-span-1">
                <label className="text-[11px] font-bold uppercase tracking-wider text-[var(--color-text-secondary)]">
                  {sourceType === "Purchase" ? t("inventory.vendorName") : t("inventory.customerName")}{" "}
                  <span className="text-rose-500">*</span>
                </label>
                <Select
                  value={sourceType === "Purchase" ? formData.vendorId : formData.customerId}
                  onValueChange={(val) =>
                    handleChange(sourceType === "Purchase" ? "vendorId" : "customerId", val)
                  }
                >
                  <SelectTrigger
                    className={`h-10 rounded-xl border bg-white text-xs font-semibold focus:ring-[var(--color-accent)] w-full ${
                      formErrors.vendorId || formErrors.customerId
                        ? "border-rose-500"
                        : "border-[var(--color-text-border)]"
                    }`}
                  >
                    <SelectValue
                      placeholder={
                        sourceType === "Purchase"
                          ? t("inventory.selectVendor")
                          : t("inventory.selectCustomer")
                      }
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {sourceType === "Purchase"
                      ? vendors.map((v) => (
                          <SelectItem key={v.id} value={v.id}>
                            {v.name}
                          </SelectItem>
                        ))
                      : customers.map((c) => (
                          <SelectItem key={c.id} value={c.id}>
                            {c.name}
                          </SelectItem>
                        ))}
                  </SelectContent>
                </Select>
                {formErrors.vendorId && (
                  <p className="text-[11px] text-rose-500 font-semibold mt-1">{formErrors.vendorId}</p>
                )}
                {formErrors.customerId && (
                  <p className="text-[11px] text-rose-500 font-semibold mt-1">{formErrors.customerId}</p>
                )}
              </div>

              {/* 11. PO Number or Project Name */}
              <div className="space-y-1.5 col-span-1">
                <label className="text-[11px] font-bold uppercase tracking-wider text-[var(--color-text-secondary)]">
                  {sourceType === "Purchase" ? t("inventory.poNumber") : t("inventory.projectName")}
                </label>
                <input
                  type="text"
                  value={sourceType === "Purchase" ? formData.poNumber : formData.projectName}
                  onChange={(e) =>
                    handleChange(sourceType === "Purchase" ? "poNumber" : "projectName", e.target.value)
                  }
                  className="w-full h-10 px-3 rounded-xl border border-[var(--color-text-border)] bg-white text-xs font-semibold focus:border-[var(--color-accent)] focus:outline-none focus:ring-2 focus:ring-[var(--color-accent)]/10"
                />
              </div>

              {/* 12. Vendor Invoice or Customer Invoice ID */}
              <div className="space-y-1.5 col-span-1">
                <label className="text-[11px] font-bold uppercase tracking-wider text-[var(--color-text-secondary)]">
                  {sourceType === "Purchase"
                    ? t("inventory.invoiceNumber")
                    : t("inventory.customerInvoiceId")}{" "}
                  {sourceType !== "Purchase" ? <span className="text-rose-500">*</span> : null}
                </label>
                <input
                  type="text"
                  value={
                    sourceType === "Purchase"
                      ? formData.vendorInvoiceNumber
                      : formData.customerInvoiceId
                  }
                  onChange={(e) =>
                    handleChange(
                      sourceType === "Purchase" ? "vendorInvoiceNumber" : "customerInvoiceId",
                      e.target.value
                    )
                  }
                  className={`w-full h-10 px-3 rounded-xl border bg-white text-xs font-semibold focus:border-[var(--color-accent)] focus:outline-none focus:ring-2 focus:ring-[var(--color-accent)]/10 ${
                    formErrors.customerInvoiceId
                      ? "border-rose-500"
                      : "border-[var(--color-text-border)]"
                  }`}
                />
                {formErrors.customerInvoiceId && (
                  <p className="text-[11px] text-rose-500 font-semibold mt-1">
                    {formErrors.customerInvoiceId}
                  </p>
                )}
              </div>

              {/* 13. Invoice Date (Purchase Only) */}
              {sourceType === "Purchase" ? (
                <div className="space-y-1.5 col-span-1">
                  <label className="text-[11px] font-bold uppercase tracking-wider text-[var(--color-text-secondary)]">
                    {t("dateTime.invoiceDate")}
                  </label>
                  <input
                    type="date"
                    value={formData.invoiceDate}
                    onChange={(e) => handleChange("invoiceDate", e.target.value)}
                    className="w-full h-10 px-3 rounded-xl border border-[var(--color-text-border)] bg-white text-xs font-semibold focus:border-[var(--color-accent)] focus:outline-none focus:ring-2 focus:ring-[var(--color-accent)]/10"
                  />
                </div>
              ) : (
                <div className="hidden md:block col-span-1" />
              )}

              {/* 14. IOF Number (Purchase Only) */}
              {sourceType === "Purchase" ? (
                <div className="space-y-1.5 col-span-1">
                  <label className="text-[11px] font-bold uppercase tracking-wider text-[var(--color-text-secondary)]">
                    {t("inventory.iofNumber")}
                  </label>
                  <input
                    type="text"
                    value={formData.iofNumber}
                    onChange={(e) => handleChange("iofNumber", e.target.value)}
                    className="w-full h-10 px-3 rounded-xl border border-[var(--color-text-border)] bg-white text-xs font-semibold focus:border-[var(--color-accent)] focus:outline-none focus:ring-2 focus:ring-[var(--color-accent)]/10"
                  />
                  <span className="text-[10px] text-gray-400 block mt-1">
                    {t("dateTime.iofNotValidated")}
                  </span>
                </div>
              ) : (
                <div className="hidden md:block col-span-1" />
              )}

              {/* 15. Warehouse Location Selection */}
              <div className="space-y-1.5 col-span-1">
                <label className="text-[11px] font-bold uppercase tracking-wider text-[var(--color-text-secondary)]">
                  {t("inventory.warehouseLocation")} <span className="text-rose-500">*</span>
                </label>
                <Select
                  value={formData.warehouseId}
                  onValueChange={(val) => handleChange("warehouseId", val)}
                >
                  <SelectTrigger
                    className={`h-10 rounded-xl border bg-white text-xs font-semibold focus:ring-[var(--color-accent)] w-full ${
                      formErrors.warehouseId ? "border-rose-500" : "border-[var(--color-text-border)]"
                    }`}
                  >
                    <SelectValue placeholder={t("inventory.selectWarehouse")} />
                  </SelectTrigger>
                  <SelectContent>
                    {warehouses.map((w) => (
                      <SelectItem key={w.id} value={w.id}>
                        {w.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {formErrors.warehouseId && (
                  <p className="text-[11px] text-rose-500 font-semibold mt-1">{formErrors.warehouseId}</p>
                )}
              </div>

              <div className="hidden md:block col-span-1" />
            </div>

            {/* Form Footer Actions Panel */}
            <div className="flex items-center justify-end gap-3 pt-6 border-t border-[var(--color-text-border)]/50">
              <button
                type="button"
                onClick={handleCancel}
                className="h-10 px-5 rounded-xl border border-[var(--color-text-border)] text-[var(--color-text-secondary)] font-bold text-xs hover:bg-gray-50 transition cursor-pointer"
              >
                {t("formActions.cancel")}
              </button>
              <button
                type="submit"
                disabled={submitting}
                className="h-10 px-6 rounded-xl bg-[var(--color-primary)] hover:bg-[var(--color-accent)] text-white font-bold text-xs transition shadow-[0_8px_20px_-8px_rgba(30,51,131,0.5)] cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitting ? t("inventory.saving") : t("formActions.saveReceipt")}
              </button>
            </div>
          </form>
        </ContentCard>
      )}
    </AppLayout>
  );
}
